import {
  ReactElement,
  SyntheticEvent,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';
import { useFormikContext } from 'formik';
import {
  Typography,
  Stack,
  AccordionSummary,
  AccordionDetails,
  TextField,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { ExpandMore } from '@mui/icons-material';
import { faMagnifyingGlass as SearchIcon } from '@fortawesome/free-solid-svg-icons';
import { useSnackbar } from 'notistack';
import { throttle } from 'lodash';

import { PlacesAutocomplete, DialogSearchCustomers } from '~components/index';
import { hasError } from '~utils/formHelpers';
import { getValidServiceType } from '~utils/order';
import { useTypePartnerCompany } from '~hooks/useTypePartnerCompany';
import { useToggle } from '~hooks/useToggle';
import { PlaceType } from '~hooks/usePlacesAutocomplete';
import { CustomerBase } from '~services/customer/types';
import { SecurityDeliveryLevel } from '~globals/types/enums';

import {
  FIELDS_NAME,
  getInitialValues,
  parseTimeWindowValue,
  SECTIONS_LABELS,
  getFieldLabel,
  getErrorsBySection,
} from '../../utils';
import { useOrderFormContext } from '../../OrderFormContext';
import { OrderFormSectionContainer } from '../../styles';
import { OrderFormIconButton, OrderFormIconButtonIcon } from './styles';

const OrderFormDestinationInformationSection = (): ReactElement => {
  const { enqueueSnackbar } = useSnackbar();
  const { isCarrier } = useTypePartnerCompany();

  const {
    values,
    errors,
    touched,
    getFieldProps,
    setFieldValue,
    setFieldTouched,
    setValues,
    submitCount,
  } = useFormikContext<ReturnType<typeof getInitialValues>>();

  const [isExpanded, toggleIsExpanded] = useToggle(true);

  const onChangeIsExpanded = useCallback(
    (_event: SyntheticEvent, expanded: boolean) => {
      toggleIsExpanded(expanded);
    },
    [toggleIsExpanded],
  );

  const currentErrorsList = useMemo(
    () => getErrorsBySection(errors, SECTIONS_LABELS.DestinationInformation),
    [errors],
  );

  useEffect(() => {
    if (submitCount > 0 && currentErrorsList.length > 0) {
      toggleIsExpanded(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentErrorsList, submitCount]);

  const {
    itemData,
    refs: { placesAutocompleteRef, serviceTypeListAutocompleteRef },
    enumSelects: { servicesTypes },
  } = useOrderFormContext();

  const [openDialogSearchCustomer, toggleOpenDialogSearchCustomer] =
    useToggle();

  const getCoordAddres = useMemo(
    () =>
      throttle<google.maps.Geocoder['geocode']>(
        (request, callback) =>
          new window.google.maps.Geocoder().geocode(request, callback),
        200,
      ),
    [],
  );

  const handleChangeAddress = useCallback(
    (_e: SyntheticEvent<Element, Event>, newValue: PlaceType | null) => {
      setFieldValue(FIELDS_NAME.ADDRESS, newValue?.description ?? '');
      setFieldValue(FIELDS_NAME.ADDRESS_COORDS, null);

      getCoordAddres({ placeId: newValue?.place_id }, (results) => {
        if (results) {
          const addresCoords = results[0].geometry.location.toJSON();

          setFieldValue(FIELDS_NAME.ADDRESS_COORDS, addresCoords);

          const zipCode = results[0].address_components
            .find((addressComponent) =>
              addressComponent.types.includes('postal_code'),
            )
            ?.long_name.replace(/\D/g, '');

          if (zipCode) {
            setFieldValue(FIELDS_NAME.ZIP_CODE, zipCode);
          }
        }
      });
    },
    [getCoordAddres, setFieldValue],
  );

  const handleBlurAddress = useCallback((): void => {
    setFieldTouched(FIELDS_NAME.ADDRESS, true);
  }, [setFieldTouched]);

  const [
    initialValueRequestPlacesAutocomplete,
    setInitialValueRequestPlacesAutocomplete,
  ] = useState(itemData?.address);

  const handleSelectCustomer = useCallback(
    (customer: CustomerBase) => {
      toggleOpenDialogSearchCustomer(false);

      setTimeout(() => {
        const customerServiceType = getValidServiceType(
          customer.serviceType,
          servicesTypes,
        );

        setValues({
          ...values,
          [FIELDS_NAME.CLIENT_NAME]: customer.legalName,
          [FIELDS_NAME.CLIENT_COD]: customer.code ?? '',
          [FIELDS_NAME.ADDRESS]: customer.address,
          [FIELDS_NAME.ADDRESS_COORDS]: {
            lat: customer.latitude,
            lng: customer.longitude,
          },
          [FIELDS_NAME.ADDRESS_NOTES]: customer.addressNotes ?? '',
          [FIELDS_NAME.ADDRESS_ACCESS_COD]: customer.accessCode ?? '',
          [FIELDS_NAME.ADDRESS_WAIT_TIME]: String(
            (customer.serviceDuration ?? 600) / 60,
          ),
          [FIELDS_NAME.CONTAC_FULL_NAME]: customer.contactName ?? '',
          [FIELDS_NAME.CONTACT_DOCUMENT]: customer.contactIdentification ?? '',
          [FIELDS_NAME.CONTACT_PHONE]: customer.contactPhone ?? '',
          [FIELDS_NAME.CONTACT_CELL_PHONE]: customer.contactMobile ?? '',
          [FIELDS_NAME.CONTACT_EMAIL]: customer.contactEmail ?? '',
          [FIELDS_NAME.CUSTOM_FIELD1]: customer.customField1 ?? '',
          [FIELDS_NAME.CUSTOM_FIELD2]: customer.customField2 ?? '',
          [FIELDS_NAME.CUSTOM_FIELD3]: customer.customField3 ?? '',
          [FIELDS_NAME.DELIVERY_APPROVE_DOCUMENT]:
            customer.validateContactIdentification ?? false,
          [FIELDS_NAME.DELIVERY_APPROVE_SECURITY_CODE]: Boolean(
            customer.securityDeliveryLevel ?? SecurityDeliveryLevel.Low,
          ),
          [FIELDS_NAME.TIME_WINDOW_ONE_FROM]: parseTimeWindowValue(
            customer.timeWindowFrom1 ?? null,
          ),
          [FIELDS_NAME.TIME_WINDOW_ONE_TO]: parseTimeWindowValue(
            customer.timeWindowTo1 ?? null,
          ),
          [FIELDS_NAME.TIME_WINDOW_SECOND_FROM]: parseTimeWindowValue(
            customer.timeWindowFrom2 ?? null,
          ),
          [FIELDS_NAME.TIME_WINDOW_SECOND_TO]: parseTimeWindowValue(
            customer.timeWindowTo2 ?? null,
          ),
          [FIELDS_NAME.TIME_WINDOW_ALL_DAY]: customer.timeWindowAllDay ?? false,
          [FIELDS_NAME.ZIP_CODE]: customer.zipCode ?? '',
          [FIELDS_NAME.STORE]: customer.store ?? '',
          [FIELDS_NAME.SERVICE_TYPE]: customerServiceType,
        });

        setInitialValueRequestPlacesAutocomplete(customer.address);

        serviceTypeListAutocompleteRef.current?.setValue(customerServiceType);

        enqueueSnackbar('Contacto frecuente cargado correctamente', {
          variant: 'success',
        });
      }, 200);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      setValues,
      values,
      toggleOpenDialogSearchCustomer,
      enqueueSnackbar,
      servicesTypes,
    ],
  );

  return (
    <OrderFormSectionContainer
      expanded={isExpanded}
      onChange={onChangeIsExpanded}
      disableGutters
      elevation={0}
      variant="outlined"
    >
      <AccordionSummary
        expandIcon={<ExpandMore />}
        aria-controls="panel-order-form-destionation-information-content"
        id="panel-order-form-destionation-information-header"
      >
        <Typography component="h6" fontWeight="bold">
          {SECTIONS_LABELS.DestinationInformation}
        </Typography>
      </AccordionSummary>

      <AccordionDetails>
        <Stack spacing={2}>
          <Stack direction="row" spacing={2}>
            <TextField
              label={getFieldLabel(FIELDS_NAME.CLIENT_NAME)}
              fullWidth
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.CLIENT_NAME)}
              {...getFieldProps(FIELDS_NAME.CLIENT_NAME)}
            />

            <OrderFormIconButton
              color="primary"
              variant="contained"
              startIcon={<OrderFormIconButtonIcon icon={SearchIcon} />}
              onClick={() => toggleOpenDialogSearchCustomer(true)}
            >
              Buscar Contacto Frecuente
            </OrderFormIconButton>
          </Stack>

          <Stack direction="row" spacing={2}>
            <TextField
              label={getFieldLabel(FIELDS_NAME.CLIENT_COD)}
              autoComplete="off"
              error={hasError(touched, errors, FIELDS_NAME.CLIENT_COD)}
              {...getFieldProps(FIELDS_NAME.CLIENT_COD)}
            />

            <FormControlLabel
              control={
                <Switch
                  {...getFieldProps(FIELDS_NAME.CLIENT_SCHEDULE)}
                  checked={values[FIELDS_NAME.CLIENT_SCHEDULE]}
                />
              }
              label={getFieldLabel(FIELDS_NAME.CLIENT_SCHEDULE)}
            />
          </Stack>

          <Stack direction="row" spacing={2}>
            <PlacesAutocomplete
              ref={placesAutocompleteRef}
              id="add-order-places-autcomplete"
              label={getFieldLabel(FIELDS_NAME.ADDRESS)}
              onChange={handleChangeAddress}
              onBlur={handleBlurAddress}
              error={hasError(touched, errors, FIELDS_NAME.ADDRESS)}
              initialValueRequest={initialValueRequestPlacesAutocomplete}
              fullWidth
            />

            <TextField
              label={getFieldLabel(FIELDS_NAME.ZIP_CODE)}
              error={hasError(touched, errors, FIELDS_NAME.ZIP_CODE)}
              fullWidth
              sx={{ maxWidth: 250 }}
              {...getFieldProps(FIELDS_NAME.ZIP_CODE)}
            />
          </Stack>

          <TextField
            label={getFieldLabel(FIELDS_NAME.ADDRESS_NOTES)}
            fullWidth
            multiline
            rows={3}
            error={hasError(touched, errors, FIELDS_NAME.ADDRESS_NOTES)}
            {...getFieldProps(FIELDS_NAME.ADDRESS_NOTES)}
          />

          <Stack direction="row" spacing={2}>
            <TextField
              label={getFieldLabel(FIELDS_NAME.STORE)}
              fullWidth
              error={hasError(touched, errors, FIELDS_NAME.STORE)}
              {...getFieldProps(FIELDS_NAME.STORE)}
            />

            {isCarrier && (
              <>
                <TextField
                  label={getFieldLabel(FIELDS_NAME.ADDRESS_ACCESS_COD)}
                  fullWidth
                  error={hasError(
                    touched,
                    errors,
                    FIELDS_NAME.ADDRESS_ACCESS_COD,
                  )}
                  {...getFieldProps(FIELDS_NAME.ADDRESS_ACCESS_COD)}
                />

                <TextField
                  label={getFieldLabel(FIELDS_NAME.ADDRESS_WAIT_TIME)}
                  fullWidth
                  autoComplete="off"
                  type="number"
                  error={hasError(
                    touched,
                    errors,
                    FIELDS_NAME.ADDRESS_WAIT_TIME,
                  )}
                  {...getFieldProps(FIELDS_NAME.ADDRESS_WAIT_TIME)}
                />
              </>
            )}
          </Stack>

          {isCarrier && (
            <Stack direction="row" spacing={2}>
              <TextField
                label={getFieldLabel(FIELDS_NAME.CUSTOM_FIELD1)}
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.CUSTOM_FIELD1)}
                {...getFieldProps(FIELDS_NAME.CUSTOM_FIELD1)}
              />

              <TextField
                label={getFieldLabel(FIELDS_NAME.CUSTOM_FIELD2)}
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.CUSTOM_FIELD2)}
                {...getFieldProps(FIELDS_NAME.CUSTOM_FIELD2)}
              />

              <TextField
                label={getFieldLabel(FIELDS_NAME.CUSTOM_FIELD3)}
                fullWidth
                error={hasError(touched, errors, FIELDS_NAME.CUSTOM_FIELD3)}
                {...getFieldProps(FIELDS_NAME.CUSTOM_FIELD3)}
              />
            </Stack>
          )}
        </Stack>
      </AccordionDetails>

      {openDialogSearchCustomer && (
        <DialogSearchCustomers
          open={openDialogSearchCustomer}
          onClose={() => toggleOpenDialogSearchCustomer(false)}
          onSelectCustomer={handleSelectCustomer}
        />
      )}
    </OrderFormSectionContainer>
  );
};

export default OrderFormDestinationInformationSection;
